import moment from 'moment';
import { WrappedCarePlan } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/CarePlan';
import { WrappedCondition } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Condition';
import { WrappedObservation } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Observation';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { WrappedServiceRequest } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ServiceRequest';
import { WrappedTask } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Task';
import {
  CodeableConcept,
  Coding,
  Reference,
  Resource,
  ServiceRequest,
  ValueSetComposeIncludeConcept,
} from 'src/careflow/ph-core/fhir-client/resources';
import { getTaskOwnerByReference } from 'src/careflow/care-coordination/task/patient/TasksGrid/TaskModal';
import { getReferenceResourceId, getValueSetConceptValue } from 'src/careflow/helpers/common';

export const internalExternalOrg: Coding[] = [
  {
    code: 'internal',
    display: 'Internal',
  },
  {
    code: 'external',
    display: 'External',
  },
  {
    code: 'all',
    display: 'All',
  },
];

const defaultUsers: any[] = [];

export interface ServiceTypePayload {
  label: string;
  value: { type: string; healthcareService: any; organizationId: string };
}

export interface ReferralPayload {
  id: string | null;
  date?: string;
  start?: string;
  status: string;
  intent: string;
  patientId?: string | null;
  assignedTo?: string;
  referredFrom?: Reference | null;
  referredTo?: string | null;
  locationOrganization?: string | null;
  category?: CodeableConcept[] | undefined | null;
  serviceType?: CodeableConcept | null;
  notes?: any[];
  services: ServiceTypePayload[] | null;
  service: ServiceTypePayload | null;
  serviceId?: string | null;
  serviceName?: string | null;
  locationId?: string;
  locationName?: string;
  instantiatesCanonical?: string[] | null;
  parentServiceRequest: WrappedServiceRequest | null | undefined;
  performerCategory?: { value: string; label: string } | null;
  reasonReference?: Reference[];
  reasonCode?: CodeableConcept[];
}

export type AddReferralPropsType = {
  title: string;
  referral?: any | null | undefined;
  patient?: WrappedPatient | null;
  serviceRequest?: WrappedServiceRequest | null;
  carePlan?: WrappedCarePlan | null;
  openAdd: boolean;
  onCancel: VoidFunction;
  onReferralSuccess?: any;
  externalValues?: any;
  conditions: WrappedCondition[] | null;
  observations: WrappedObservation[] | null;
  task?: WrappedTask | null;
  showWorkflowScope?: boolean;
};

export const defaultStateFieldsValues = (
  id: string,
  referral: WrappedServiceRequest | null,
  additionalData?: {
    intentValueSet: ValueSetComposeIncludeConcept[];
    categoryValueSet: ValueSetComposeIncludeConcept[];
    statusValueSet: ValueSetComposeIncludeConcept[];
    performerCategoryValues: { value: string; label: string }[] | undefined;
    conditions: WrappedCondition[] | null;
    observations: WrappedObservation[] | null;
    observationSuggestions: WrappedObservation[];
    conditionSuggestions: WrappedCondition[];
  }
) => {
  const {
    intentValueSet,
    statusValueSet,
    performerCategoryValues,
    conditions,
    observations,
    observationSuggestions,
    conditionSuggestions,
  } = additionalData ?? {};

  const intent = getValueSetConceptValue(intentValueSet ?? [], referral?.intent) ?? null;
  const status = getValueSetConceptValue(statusValueSet ?? [], referral?.status) ?? null;
  const locationId = referral?.locationReference?.[0]?.reference?.split?.('/')?.[1];
  const locationName = referral?.locationReference?.[0]?.display;
  const getPerformerCategory = performerCategoryFromMetaTag(referral, performerCategoryValues);
  const isInternalPractitionerReferral = getPerformerCategory?.value === 'practitioner';

  const performer = isInternalPractitionerReferral
    ? referral?.performer?.find?.(
        (performer) => performer?.reference?.split?.('/')?.[0] === 'Practitioner'
      )
    : referral?.performer?.find?.(
        (performer) => performer?.reference?.split?.('/')?.[0] !== 'HealthcareService'
      );

  let reasonReference: (WrappedCondition | WrappedObservation)[] = [];

  if (referral?.reasonReference?.length) {
    const existingConditionRefs = referral.reasonReference.filter((x) =>
      x?.reference?.startsWith('Condition')
    );

    const existingObservationRefs = referral.reasonReference.filter((x) =>
      x?.reference?.startsWith('Observation')
    );

    if (conditions && existingConditionRefs.length > 0) {
      reasonReference = reasonReference.concat(getConditionsByServiceRequest(referral, conditions));
    }

    if (observations && existingObservationRefs.length > 0) {
      reasonReference = reasonReference.concat(
        getObservationsByServiceRequest(referral, observations)
      );
    }
  }

  if (conditionSuggestions?.length) {
    reasonReference = reasonReference.concat(
      conditionSuggestions.filter(
        (cond) => !reasonReference.some((existing) => existing?.id === cond?.id)
      )
    );
  }

  if (observationSuggestions?.length) {
    reasonReference = reasonReference.concat(
      observationSuggestions.filter(
        (obs) => !reasonReference.some((existing) => existing?.id === obs?.id)
      )
    );
  }

  const getReferredToItem = getTaskOwnerByReference(performer) ?? null;

  let referredToItemValue: any = null;
  if (performer && getReferredToItem) {
    referredToItemValue = getReferredToItem;
  } else if (performer?.display) {
    referredToItemValue = {
      label: performer.display,
      value: performer.display,
      isAddOption: true,
    };
  } else if (performer?.reference) {
    referredToItemValue = {
      reference: performer.reference,
      display: performer.reference.split('/')[1],
      isAddOption: false,
    };
  }

  return {
    intent,
    category: referral?.category?.map((e) => e?.coding?.[0]) ?? [],
    date: referral?.occurrenceDateTime
      ? moment(referral?.occurrenceDateTime)?.format('YYYY-MM-DD')
      : moment().format('YYYY-MM-DD'),
    startHour: referral?.occurrenceDateTime
      ? moment(referral?.occurrenceDateTime)?.format('HH:mm')
      : moment().format('HH:mm'),
    referredFromItem: referral?.requester ? getTaskOwnerByReference(referral?.requester) : null,
    referredToItem: referredToItemValue,
    orgnization: null,
    organizaionItem: null,
    service: referral?.code?.coding?.[0].code ?? null,
    serviceTypeItem: null,
    instantiatesCanonical: null,
    status: status?.code,
    statusItem: status
      ? {
          display: status?.display,
          code: status?.code,
        }
      : null,
    users: defaultUsers,
    assignedToItem: referral?.getPerfomerByType?.('Practitioner')?.reference
      ? {
          label: referral.getPerfomerByType('Practitioner')?.display,
          value: referral.getPerfomerByType('Practitioner')?.reference,
        }
      : null,
    locationOrganization: locationId
      ? {
          label: locationName,
          value: {
            location: { id: locationId, name: locationName },
          },
        }
      : null,
    file: '',
    selectedFile: null,
    note: referral ? referral?.note?.[0]?.text : '',
    isLoading: false,
    success: null,
    openSearch: false,
    error: null,
    isSearchMemberOpen: false,
    inputSelected: null,
    performerCategory: getPerformerCategory?.value ? getPerformerCategory : null,
    addresses: reasonReference ?? [],
  };
};

export const REFERRAL_CREATE_ERROR_MESSAGE = 'Error saving the referral. Please try again';

export const getConditionsByServiceRequest = (
  serviceRequest: WrappedServiceRequest,
  conditions: WrappedCondition[]
): WrappedCondition[] => {
  const addressesIds =
    serviceRequest.reasonReference
      ?.filter((x) => x?.reference?.startsWith('Condition'))
      ?.map((x) => getReferenceResourceId(x?.reference || '')) ?? [];
  if (!addressesIds || addressesIds.length === 0) return [];
  return conditions.filter((x) => x?.id && addressesIds.indexOf(x?.id) > -1);
};

export const getObservationsByServiceRequest = (
  serviceRequest: WrappedServiceRequest,
  observations: WrappedObservation[]
): WrappedObservation[] => {
  const addressesIds =
    serviceRequest.reasonReference
      ?.filter((x) => x?.reference?.startsWith('Observation'))
      ?.map((x) => getReferenceResourceId(x?.reference || '')) ?? [];
  if (!addressesIds || addressesIds.length === 0) return [];
  return observations.filter((x) => x?.id && addressesIds.indexOf(x?.id) > -1);
};

export const performerCategoryFromMetaTag = (
  serviceRequest: WrappedServiceRequest | ServiceRequest | null,
  performerCategoryValues: { value: string; label: string }[] | undefined
) => {
  const metaTagPerformerCategoryCode = serviceRequest?.meta?.tag?.find((e) =>
    e?.system?.includes('ph-service-request-performer-category')
  )?.code;
  const getPerformerCategory = performerCategoryValues?.find(
    (e) => e?.value === metaTagPerformerCategoryCode
  );
  return getPerformerCategory;
};

type Filters<T extends Resource> = {
  [K in T['resourceType']]?: {
    values: string[];
    extractor: (item: Extract<T, { resourceType: K }>) => string | undefined;
  };
};

export function filterResources<T extends Resource>(
  items: T[] | undefined,
  filters: Filters<T>
): T[] {
  if (!items?.length) return [];

  return items.reduce<T[]>((acc, item) => {
    const filter = filters[item.resourceType as keyof Filters<T>];
    if (filter && filter?.values?.length === 0) {
      acc.push(item);
      return acc;
    }

    const value = filter?.extractor(item as any);
    if (value && filter?.values.includes(value)) {
      acc.push(item);
    }

    return acc;
  }, []);
}
